// 通用 IndexedDB 缓存工具（带版本号 + TTL 失效）
// 用于缓存较慢的接口数据，避免每次进入都重新拉取。
//
// 失效规则：缓存版本号不匹配（数据结构变更/强制刷新）或超过 TTL 时，重新拉取。

const DB_NAME = 'xihu_citywalk';
const DB_VERSION = 1;
const STORE = 'kv';
// 默认缓存有效期 12h（展厅每日开机即相当于每天刷新一次数据）
const DEFAULT_TTL_MS = 12 * 60 * 60 * 1000;

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function idbGet<T>(key: string): Promise<T | undefined> {
  try {
    const db = await openDB();
    return await new Promise<T | undefined>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const req = tx.objectStore(STORE).get(key);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  } catch (e) {
    return undefined;
  }
}

export async function idbSet(key: string, value: unknown): Promise<void> {
  try {
    const db = await openDB();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).put(value, key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch (e) {
    // IndexedDB 不可用或写入失败时静默降级，不影响功能
  }
}

export async function idbDel(key: string): Promise<void> {
  try {
    const db = await openDB();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).delete(key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch (e) {
    // 忽略
  }
}

// 缓存信封：附带版本号与时间戳，用于失效判断
export interface CacheEnvelope<T> {
  version: number;
  timestamp: number;
  data: T;
}

export function isEnvelopeValid<T>(
  env: CacheEnvelope<T> | undefined,
  version: number,
  ttlMs: number,
): env is CacheEnvelope<T> {
  return !!env && env.version === version && Date.now() - env.timestamp < ttlMs;
}

interface WithCacheOptions {
  version?: number;
  ttlMs?: number;
}

// 高阶缓存：命中且未失效则返回缓存，否则执行 fetcher 并写入缓存。
// 注意：fetcher 返回 null/undefined（失败/无数据）时不写缓存，下次会重试。
export async function withCache<T>(
  key: string,
  fetcher: () => Promise<T>,
  options?: WithCacheOptions,
): Promise<T> {
  const version = options?.version ?? 1;
  const ttlMs = options?.ttlMs ?? DEFAULT_TTL_MS;

  const env = await idbGet<CacheEnvelope<T>>(key);
  if (isEnvelopeValid(env, version, ttlMs)) return env.data;

  const data = await fetcher();
  // 失败/空结果不写缓存：避免服务端瞬时返回空把空数据锁住整个 TTL
  const isEmpty = data == null || (Array.isArray(data) && data.length === 0);
  if (!isEmpty) {
    const envelope: CacheEnvelope<T> = { version, timestamp: Date.now(), data };
    await idbSet(key, envelope);
  }
  return data;
}

// 手动清除某个缓存键（数据更新后想强制重新拉取时调用）
export const clearCacheKey = (key: string) => idbDel(key);
